import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import NavBar from './components/nav/NavBar';
import Footer from './components/Footer/Footer';
import { AuthProvider } from './context/AuthContext';
import { NieveProvider } from './context/NieveContext';
import SplashMundial from './components/splash/SplashMundial';
import Nieve from './components/decoracion/Nieve';
import RutaCargando from './components/rutas/RutaCargando';
import ErrorDeCarga from './components/rutas/ErrorDeCarga';

import 'animate.css/animate.min.css';

import 'primereact/resources/themes/saga-blue/theme.css';  // or any other theme
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import 'primereact/resources/themes/lara-light-blue/theme.css'; // o el tema que prefieras
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css'; // para clases utilitarias (como flexbox, paddings, etc.)

/* ───────────────────────────────────────────────────────────────────────────
   P4 · Code splitting por ruta — decidido 20/08/2026
   Ver docs/frontend/plan-mejoras-comercial.md § P4

   QUÉ QUEDA EN EL BUNDLE INICIAL
   Solo HomeComercial. Es la ruta '/' y la única página que un visitante nuevo
   ve antes de decidir si se queda: es donde se mide el LCP y donde el dato de
   nivel A (Vodafone, A/B con el 50% del tráfico: LCP −31% ⇒ ventas +8%) tiene
   efecto sobre el negocio. Todo lo demás que viva en el bundle inicial es peso
   que la home paga sin usar.

   QUÉ SE DIFIERE
   Todo el resto: el panel de gestión completo (MUI, @mui/x-data-grid,
   PrimeReact, Leaflet, exceljs, jspdf, tesseract, chart.js) y también las
   páginas institucionales públicas.

   POR QUÉ TAMBIÉN LAS PÚBLICAS
   Diferir una ruta NO la hace más lenta de manera gratuita: le agrega un
   round-trip a quien navega hasta ella. Pero ese round-trip se paga sobre un
   bundle base mucho más chico, así que quien entra directo a una página
   institucional descarga menos bytes en total que hoy. Lo que sí sería un
   error es diferir '/': ahí el chunk extra se sumaría al camino crítico de la
   primera pantalla, que es justamente lo que P4 viene a arreglar.

   EL RIESGO QUE HAY QUE RESPETAR
   Un <Suspense> sin fallback, o mal ubicado, deja la ruta en blanco y eso pasa
   el build. Acá el fallback es obligatorio (RutaCargando) y el boundary está
   dentro de AppLayout, de modo que NavBar y Footer siguen en pantalla mientras
   el chunk de la ruta viaja. ErrorDeCarga evita que un chunk que no descarga
   tumbe el árbol entero.
   ─────────────────────────────────────────────────────────────────────────── */

// ── Sitio comercial: EAGER. Es la primera pantalla, no se difiere.
import HomeComercial from './paginas/comercial/HomeComercial';

// ── Institucional público
const Inicio = lazy(() => import('./paginas/inicio/Inicio'));
const JuegoOnline = lazy(() => import('./paginas/JuegoOnline'));
const JuegoResponsable = lazy(() => import('./paginas/JuegoResponsable'));
const JuegoIlegal = lazy(() => import('./paginas/JuegoIlegal'));
const RecomendacionesJuegoIlegal = lazy(() => import('./paginas/RecomendacionesJuegoIlegal'));
const Noticias = lazy(() => import('./paginas/Noticias'));
const NoticiaDetail = lazy(() => import('./components/cargarNoticias/Noticias'));
const InstitucionalMision = lazy(() => import('./paginas/InstitucionalMision'));
const InstitucionalAutoridades = lazy(() => import('./paginas/InstitucionalAutoridades'));
const InstitucionalDelegaciones = lazy(() => import('./paginas/institucionaldelegaciones'));
const Agencias = lazy(() => import('./paginas/Agencias'));
const RealizacionBingos = lazy(() => import('./paginas/RealizacionBingos'));
const DondeJuego = lazy(() => import('./paginas/DondeJuego'));
const Lavadodeactivos = lazy(() => import('./paginas/LavadoDeActivos'));
const Certificados = lazy(() => import('./paginas/noticias/Certificados'));
const Diplomatura = lazy(() => import('./paginas/noticias/Diplomatura'));
const Enconstruccion = lazy(() => import('./paginas/Enconstruccion'));
const Reglamentos = lazy(() => import('./paginas/Reglamentos'));
const LugaresdeAyuda = lazy(() => import('./paginas/LugaresdeAyuda'));
const ComoTrabajamos = lazy(() => import('./paginas/ComoTrabajamos'));
const Quetaljuegoyo = lazy(() => import('./paginas/Quetaljuegoyo'));
const SitiosAutorizados = lazy(() => import('./paginas/SitiosAutorizados'));
const Mitosyverdades = lazy(() => import('./paginas/Mitosyverdades'));
const Recomendaciones = lazy(() => import('./paginas/Recomendaciones'));
const Denuncias = lazy(() => import('./paginas/Denuncias'));
const Descargas = lazy(() => import('./paginas/descargas/Descargas'));
const Calendariodejuegos = lazy(() => import('./paginas/Calendariodejuegos'));
const Videounamas = lazy(() => import('./paginas/Videounamas'));
const SorteoEnVivo = lazy(() => import('./paginas/SorteoEnVivo'));
const Encuesta9DeJulio = lazy(() => import('./paginas/encuestas/Encuesta9DeJulio'));

// ── Prevención de lavado de activos
const ManualProcedimientos = lazy(() => import('./paginas/lavadodeactivos/ManualProcedimientos'));
const PersonasExpuestas = lazy(() => import('./paginas/lavadodeactivos/PersonasExpuestas'));
const PorqueDeLaPrevencion = lazy(() => import('./paginas/lavadodeactivos/PorqueDeLaPrevencion'));
const QueEsGafi = lazy(() => import('./paginas/lavadodeactivos/QueEsGafi'));
const NormativaComplementaria = lazy(() => import('./paginas/lavadodeactivos/NormativaComplementaria'));
const GuiaCobro = lazy(() => import('./paginas/lavadodeactivos/GuiaCobro'));

// ── Concurso "Una más"
const QuienesPuedenParticipar = lazy(() => import('./paginas/unaMas/QuienesPuedenParticipar'));
const ComoCargarCupon = lazy(() => import('./paginas/unaMas/ComoCargarCupon'));
const CuandoSortea = lazy(() => import('./paginas/unaMas/CuandoSortea'));
const Premios = lazy(() => import('./paginas/unaMas/Premios'));
const Ganadoresum = lazy(() => import('./paginas/unaMas/Ganadoresum'));
const GaleriaFotos = lazy(() => import('./paginas/unaMas/GaleriaFotos'));
const Concurso = lazy(() => import('./components/concurso/Concurso'));
const Browser = lazy(() => import('./components/browserConcurso/Browser'));

// ── Cuenta
const Login = lazy(() => import('./paginas/Login'));
const Register = lazy(() => import('./paginas/Register'));
const Reset = lazy(() => import('./paginas/Reset'));
const ProfileForm = lazy(() => import('./components/ProfileForm'));

// ── Formularios / Autoexclusión (N4: no se degrada)
const Formularios = lazy(() => import('./components/Formularios/Formularios'));
const FormularioAuto = lazy(() => import('./components/Formularios/FormularioAuto'));
const InformeAistencia = lazy(() => import('./components/Formularios/InformeAsistencia'));
const AutoexcluidosLookup = lazy(() => import('./components/Formularios/AutoexcluidosLookup'));
const AutoexcluidosGallery = lazy(() => import('./components/Formularios/AutoexcluidosGallery'));
const ModificarAutoexcluido = lazy(() => import('./components/autoe/ModificarAutoexcluido'));

// ── Cupones y sorteos
const NoGanadores = lazy(() => import('./components/cuponesNo/NoGanadores'));
const BDusuarios = lazy(() => import('./components/cuponesNo/BaseDados'));
const Cupones = lazy(() => import('./components/cuponesNo/Cupones'));
const RankingCupones = lazy(() => import('./components/cuponesNo/RankingCupones'));
const RankingCuponesMes = lazy(() => import('./components/cuponesNo/RankingCuponesMes'));
const VerGanador = lazy(() => import('./components/cuponesNo/VerGanador'));
const AddSorteoForm = lazy(() => import('./components/cuponesNo/AddSorteoForm'));

// ── Carga de contenido
const CargarSlider = lazy(() => import('./components/cargarSlider/CargarSlider'));
const CargarNoticia = lazy(() => import('./components/cargarNoticias/CargarNoticia'));
const CargaProgramaSorteo = lazy(() => import('./components/cargarProgramas/CargaProgramaSorteo'));

// ── Panel de gestión (MUI + x-data-grid: el grueso del peso)
const Dashboard = lazy(() => import('./components/dashboard/Dasboard'));
const BDEmpleados = lazy(() => import('./components/dashboard/BDEmpleados'));
const BDAutoexcluidos = lazy(() => import('./components/dashboard/BDAutoexcluidos'));
const ConcursoRespEditForm = lazy(() => import('./components/dashboard/ConcursoRespEditForm'));
const UserDocumentsGrid = lazy(() => import('./components/dashboard/concursovideosusuarios'));
const YoutubeViewer = lazy(() => import('./components/dashboard/YoutubeViewer'));
const ConcursoResppanel = lazy(() => import('./components/dashboard/DashConcurso'));
const ConcursoResp = lazy(() => import('./components/dashboard/ConcursoRespo'));
const Bases = lazy(() => import('./components/dashboard/concursovideos/bases'));

// ── Estadísticas (chart.js, papaparse, xlsx)
const CsvUploader = lazy(() => import('./components/estadisticas/CsvUploader'));
const EstadisticasPanel = lazy(() => import('./components/estadisticas/EstadisticasPanel'));
const VentasPorAgencia = lazy(() => import('./components/estadisticas/VentasPorAgencia'));
const VentasPorAgenciaYJuego = lazy(() => import('./components/estadisticas/VentasPorAgenciaYJuego'));
const VentasPorHoraYJuego = lazy(() => import('./components/estadisticas/VentasPorHoraYJuego'));
const VentasPorHora = lazy(() => import('./components/estadisticas/VentasPorHora'));
const VentasPorJuego = lazy(() => import('./components/estadisticas/VentasPorJuego'));

function AppLayout({ children }) {
  const location = useLocation();
  // Rutas a pantalla completa: sin NavBar/Footer globales (traen los propios)
  const fullBleed = location.pathname === '/Dashboard' || location.pathname === '/' || location.pathname === '/comercial';

  // key={pathname}: si una ruta falla al cargar, el boundary se remonta al
  // navegar a otra y el sitio no queda trabado en el mensaje de error.
  if (fullBleed) {
    return (
      <ErrorDeCarga key={location.pathname}>
        <Suspense fallback={<RutaCargando />}>{children}</Suspense>
      </ErrorDeCarga>
    );
  }

  return (
    <>
      <NavBar />
      <div className="margen">
        <ErrorDeCarga key={location.pathname}>
          <Suspense fallback={<RutaCargando />}>{children}</Suspense>
        </ErrorDeCarga>
      </div>
      <Footer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <NieveProvider>
      <div className="App">
        <SplashMundial />
        <Nieve />
          <Router>
            <AppLayout>
              <Routes>
                <Route path='/' element={<HomeComercial />} />
                <Route path='/institucional-home' element={<Inicio />} />
                <Route path='/juegoonline' element={<JuegoOnline />} />
                <Route path='/juegoResponsable' element={<JuegoResponsable />} />
                <Route path='/slider' element={<CargarSlider />} />
                <Route path='/browser' element={<Browser />} />
                {/*           <Route path='/juegoilegal' element={<JuegoIlegal />} /> */}
                <Route path='/noticias' element={<Noticias />} />
                <Route path='/institucionalMision' element={<InstitucionalMision />} />
                <Route path='/mision' element={<InstitucionalMision />} />
                <Route path='/autoridades' element={<InstitucionalAutoridades />} />
                <Route path='/delegaciones' element={<InstitucionalDelegaciones />} />
                <Route path='/agencias' element={<Agencias />} />
                <Route path='/realizacionbingos' element={<RealizacionBingos />} />
                <Route path='/dondejuego' element={<DondeJuego />} />
                <Route path='/lavadodeactivos' element={<Lavadodeactivos />} />
                <Route path='/certificados' element={<Certificados />} />
                <Route path='/diplomatura' element={<Diplomatura />} />
                <Route path='/videounamas' element={<Videounamas />} />
                <Route path='/formulario' element={<Formularios />} />
                <Route path='/formauto' element={<FormularioAuto />} />
                <Route path='/comotrabajamos' element={<ComoTrabajamos />} />
                <Route path='/quetaljuego' element={<Quetaljuegoyo />} />
                <Route path='/sitiosautorizados' element={<SitiosAutorizados />} />
                <Route path='/mitosyverdades' element={<Mitosyverdades />} />
                <Route path='/recomendaciones' element={<Recomendaciones />} />
                <Route path='/reglamentos' element={<Reglamentos />} />
                <Route path='/lugaresdeayuda' element={<LugaresdeAyuda />} />
                <Route path='/enconstruccion' element={<Enconstruccion />} />
                <Route path='/register' element={<Register />} />
                <Route path='/login' element={<Login />} />
                <Route path='/reset' element={<Reset />} />
                <Route path='/noganadores' element={<NoGanadores />} />
                <Route path='/verganador' element={<VerGanador />} />
                <Route path='/profile' element={<ProfileForm />} />
                <Route path='/comocargarcupon' element={<ComoCargarCupon />} />
                <Route path='/cuandosortea' element={<CuandoSortea />} />
                <Route path='/premios' element={<Premios />} />
                <Route path='/quienespueden' element={<QuienesPuedenParticipar />} />
                <Route path='/juegoilegal' element={<JuegoIlegal />} />
                <Route path='/denuncias' element={<Denuncias />} />
                <Route path='/ganadoresum' element={<Ganadoresum />} />
                <Route path='/formularioparticipacion' element={<Concurso />} />
                <Route path='/galeriafotos' element={<GaleriaFotos />} />
                <Route path='/cargarslider' element={<CargarSlider />} />
                <Route path='/BDusuarios' element={<BDusuarios />} />
                <Route path='/cupones/:id/:ns' element={<Cupones />} />
                <Route path='/ranking' element={<RankingCupones />} />
                <Route path='/rankingmes/:nsorteo' element={<RankingCuponesMes />} />
                <Route path='/cargarnoticia' element={<CargarNoticia />} />
                <Route path='/noti/:id' element={<NoticiaDetail />} />
                <Route path='/descargas' element={<Descargas />} />
                <Route path='/calendariojuegos' element={<Calendariodejuegos />} />
                <Route path='/AutoexcluidosLookup' element={<AutoexcluidosLookup />} />
                <Route path='/AutoexcluidosGallery' element={<AutoexcluidosGallery />} />
                <Route path="/ModificarAutoexcluido/:dni" element={<ModificarAutoexcluido />} />
                <Route path='/AddSorteo' element={<AddSorteoForm />} />
                <Route path='/Dashboard' element={<Dashboard />} />
                <Route path='/BDEmpleados' element={<BDEmpleados />} />
                <Route path='/BDAutoexcluidos' element={<BDAutoexcluidos />} />
                <Route path='/InformeAsistencia' element={<InformeAistencia />} />
                <Route path='/ConcursoRespEditForm/:id' element={<ConcursoRespEditForm />} />
                <Route path='/UserDocumentsGrid' element={<UserDocumentsGrid />} />
                <Route path='/YoutubeViewer/:id' element={<YoutubeViewer />} />
                <Route path='/ConcursoResppanel' element={<ConcursoResppanel />} />
                <Route path='/ConcursoResp' element={<ConcursoResp />} />
                <Route path='/bases' element={<Bases/>}/>
                <Route path='/RecomendacionesJuegoIlegal' element={<RecomendacionesJuegoIlegal/>}/>
                <Route path='/manualprocedimientos' element={<ManualProcedimientos/>}/>
                <Route path='/personasexpuestas' element={<PersonasExpuestas/>}/>
                <Route path='/porquedelaprevencion' element={<PorqueDeLaPrevencion/>}/>
                <Route path='/queesgafi' element={<QueEsGafi/>}/>
                <Route path='/normativacomplementaria' element={<NormativaComplementaria/>}/>
                <Route path='/guiacobro' element={<GuiaCobro/>}/>
                <Route path='/CsvUploader' element={<CsvUploader />} />
                <Route path='/EstadisticasPanel' element={<EstadisticasPanel />} />
                <Route path='/VentasPorAgencia' element={<VentasPorAgencia />} />
                <Route path='/VentasPorAgenciaYJuego' element={<VentasPorAgenciaYJuego />} />
                <Route path='/VentasPorHoraYJuego' element={<VentasPorHoraYJuego />} />
                <Route path='/VentasPorHora' element={<VentasPorHora />} />
                <Route path='/VentasPorJuego' element={<VentasPorJuego />} />
                <Route path='/CargaProgramaSorteo' element={<CargaProgramaSorteo />} />
                <Route path='/Encuesta9DeJulio' element={<Encuesta9DeJulio />} />
                <Route path='/sorteo-en-vivo' element={<SorteoEnVivo />} />
                <Route path='/comercial' element={<HomeComercial />} />

              </Routes>
            </AppLayout>
          </Router>
      </div>
      </NieveProvider>
    </AuthProvider>
  );
}

export default App;
